import { Box } from "@chakra-ui/react";
import type { BoxProps } from "@chakra-ui/react";
import { useRef, useState } from "react";

import { useEventListener } from "@nens/react/hooks";
import { removeUndefinedObj } from "@nens/shared-utils";

import { BASE_PAGE_OVERFLOW_ID } from "@/components/base-page";

interface StickyWrapperProps extends Omit<BoxProps, "children"> {
  isSticky: boolean;
  children: React.ReactNode | ((hasReachedStickyPosition: boolean) => React.ReactNode);
}

export const StickyWrapper = ({ isSticky, children, ...rest }: StickyWrapperProps) => {
  const stickyTriggerRef = useRef<HTMLDivElement | null>(null);
  const [hasReachedStickyPosition, setHasReachedStickyPosition] = useState(false);
  const cleanRest = removeUndefinedObj(rest);

  const scrollContainer = document.getElementById(BASE_PAGE_OVERFLOW_ID);

  const updateStickyPositionState = () => {
    if (!isSticky || !stickyTriggerRef.current || !scrollContainer) return;
    const triggerTop = stickyTriggerRef.current.getBoundingClientRect().top;
    const scrollTop = scrollContainer.scrollTop;
    setHasReachedStickyPosition(triggerTop <= scrollTop);
  };

  useEventListener("scroll", updateStickyPositionState, scrollContainer);

  if (!isSticky) {
    return (
      <Box position="relative" {...cleanRest}>
        {typeof children === "function" ? children(false) : children}
      </Box>
    );
  }

  return (
    <>
      <Box
        ref={stickyTriggerRef}
        position="absolute"
        width={0}
        height={0}
        pointerEvents="none"
        visibility="hidden"
      />
      <Box
        position="sticky"
        top={2}
        left={0}
        zIndex={11}
        rounded="base"
        transition="box-shadow 0.2s, background-color 0.2s, border-width 0.2s"
        bg={hasReachedStickyPosition ? "brand.bg" : "transparent"}
        boxShadow={hasReachedStickyPosition ? "default" : "none"}
        borderWidth={hasReachedStickyPosition ? "base" : undefined}
        {...cleanRest}
      >
        {typeof children === "function" ? children(hasReachedStickyPosition) : children}
      </Box>
    </>
  );
};
